import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import toast from "react-hot-toast";
import { useCallback, useEffect, useState } from "react";

import { useAllProducts } from "../../data";
import { parseAlertLimit } from "../../data/alerts/utils";
import { useDeleteAlerts, useUpdateAlert } from "../../data/alerts";
import { Button, ToastNotification } from "@artis/ui";
import { Alert } from "../ui";
import { vapidKeys } from "../../globals";
import { alertSounds } from "../globals";
import { useUserPushSubscriptions } from "../../data/alerts";
import type { TAlert } from "../../data/alerts/queries";
import {
  collapseProductChangeAlerts,
  lostFolderPackageIds,
  mergeProductChangeAlertRows,
  PARTIAL_FORMULA_METADATA_MS,
  productChangeSessionKey,
  productChangeSidebarKey,
  resolvePartialFormulaMetadataChangeType,
  type PartialFormulaMetadataEntry,
  isCurveChangeType,
  isPermissionChangeType,
  type ProductChangeAlert,
} from "../calcs/lib/features/product-alerts";
import {
  bottomRightToastOptions,
  registerBottomRightToastDismiss,
  removeBottomRightToast,
} from "../components/bottomRightToastStack";
import { CellAlertToast } from "./CellAlertToast";
import { useNotificationCentreEnabled } from "./feature-flag";
import {
  aggregateOpenProductChangeToast,
  getCurveChangeToastCopy,
  productChangeToastCurveCount,
  productChangeToastTitle,
  splitProductChangeAlerts,
} from "./productChangeCards";
import {
  buildPriceAlertEntry,
  dropPriceAlertsForFolders,
  getAlertDetails,
  mergePriceAlertRows,
  priceAlertBodyForDisplay,
  priceAlertFiringKey,
  priceAlertsToRetireOnToastDismiss,
  rememberHandledPriceAlertFirings,
  type PriceAlertSidebarEntry,
} from "./priceAlerts";
import { closeNotificationsPanel, useSidebarState } from "./sidebarState";
import { WhatsNewModal } from "./WhatsNewSection";
import {
  dismissAnnouncementToasts,
  insertViewedAnnouncement,
  useToastableAnnouncements,
  useViewedAnnouncements,
  type WhatsNewFeature,
} from "../infoSection/hooks";
import { useGraphQLClient } from "@package/graphql/react";
import { useUserId } from "../../context/auth/hooks";

// Temporary debounce fix to prevent duplicate notifications from multiple workers.
const ALERT_SOUND_DELAY = 5000;
const alertHistory: Record<string, number> = {};
const PRODUCT_CHANGE_TOAST_ID = "product-alert-batch";
const PRICE_ALERT_TOAST_ID = "price-alert-batch";
const productAlertToastIds = new Set<string>();
const priceAlertToastIds = new Set<string>();
/** Keyed per firing, so the same firing can't toast twice while a genuine re-trigger still can. */
const reportedPriceAlertFirings = new Set<string>();
const reportedProductChangeKeys = new Set<string>();
const partialFormulaMetadataBySession = new Map<string, PartialFormulaMetadataEntry>();
const lastProductChangeAtBySidebarKey = new Map<string, number>();
/** Folders named by the currently open product-change toast, if any. */
const toastedFolderKeys = new Set<string>();
/** Curve-change cards currently shown in the open toast — a later curve is merged in, not replaced. */
let toastedProductChanges: ProductChangeAlert[] = [];
/** Folders named by the currently open price-alert toast, if any. */
const toastedPriceAlertFolderKeys = new Set<string>();
/** Cards currently shown in the open price-alert toast — a later firing is merged in, not replaced. */
let toastedPriceAlerts: PriceAlertSidebarEntry[] = [];

const upgradeIncomingProductChanges = (products: ProductChangeAlert[]): ProductChangeAlert[] =>
  products.map((item) => {
    const sessionKey = productChangeSessionKey(item);
    const changeType = resolvePartialFormulaMetadataChangeType(partialFormulaMetadataBySession, sessionKey, {
      formula: item.changeType === "formula" || item.changeType === "both",
      metadata: item.changeType === "metadata" || item.changeType === "both",
    });
    return changeType ? { ...item, changeType } : item;
  });

const formatCurveNamesSummary = (products: ProductChangeAlert[]) => {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const product of products) {
    const key = productChangeSessionKey(product);
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(product.name);
  }
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  const others = names.length - 2;
  return `${names[0]}, ${names[1]} and ${others} other${others !== 1 ? "s" : ""}`;
};

const dismissProductAlertToast = (id: string) => {
  removeBottomRightToast(id);
  productAlertToastIds.delete(id);
  toastedFolderKeys.clear();
  toastedProductChanges = [];
};

const dismissPriceAlertToast = (id: string) => {
  removeBottomRightToast(id);
  priceAlertToastIds.delete(id);
  toastedPriceAlertFolderKeys.clear();
  toastedPriceAlerts = [];
};

/** Lets these alerts raise a toast again, whichever firing they were last reported for. */
export const forgetReportedPriceAlerts = (ids: string[]) => {
  const prefixes = ids.map((id) => `${id}:`);
  for (const key of reportedPriceAlertFirings) {
    if (prefixes.some((prefix) => key.startsWith(prefix))) reportedPriceAlertFirings.delete(key);
  }
};

/**
 * Closes the batched price alert toast, keeping its firings on the reported list: another client's
 * worker re-emits the same firing, which would otherwise put the toast straight back after the user
 * closed it.
 */
const closePriceAlertToast = () => {
  dismissPriceAlertToast(PRICE_ALERT_TOAST_ID);
};

/**
 * Takes these cards off the list and records their firings as dealt with. The record is what makes
 * the removal stick: only a later firing of the same alert can put a card back.
 */
export const useClearPriceAlerts = () => {
  const [, setSidebarState] = useSidebarState();
  return useCallback(
    (entries: PriceAlertSidebarEntry[]) => {
      if (entries.length === 0) return;
      const ids = new Set(entries.map((entry) => entry.id));
      setSidebarState((prev) => {
        // The stored card may carry a newer firing than the caller's snapshot (a toast keeps the
        // entries it opened with), so what's actually on the list is retired too.
        const stored = prev.priceAlerts.filter((entry) => ids.has(entry.id));
        return {
          ...prev,
          priceAlerts: prev.priceAlerts.filter((entry) => !ids.has(entry.id)),
          handledPriceAlertFirings: rememberHandledPriceAlertFirings(prev.handledPriceAlertFirings ?? [], [
            ...entries,
            ...stored,
          ]),
        };
      });
    },
    [setSidebarState],
  );
};

/**
 * Clearing the list is reading it, and a fired non-recurring alert has nothing left to do, so it is
 * deleted rather than left behind as an inactive row in Manage Alerts — the same end the toast's X
 * reaches. Recurring alerts stay and re-arm.
 */
export const useRetireReadPriceAlerts = () => {
  const deleteAlerts = useDeleteAlerts();
  const clearPriceAlerts = useClearPriceAlerts();
  return useCallback(
    (entries: PriceAlertSidebarEntry[]) => {
      clearPriceAlerts(entries);
      for (const entry of entries) {
        if (!entry.recurring) deleteAlerts.mutate([entry.id]);
      }
    },
    [clearPriceAlerts, deleteAlerts],
  );
};

/** Closes the panel; viewed (accordion still open) price alerts are marked read, including spent one-offs. */
export const useCloseNotificationsPanel = () => {
  const [sidebarState, setSidebarState] = useSidebarState();
  const deleteAlerts = useDeleteAlerts();

  return useCallback(() => {
    if (sidebarState.initialPriceAlertsExpanded) {
      for (const entry of sidebarState.priceAlerts) {
        if (!entry.recurring) deleteAlerts.mutate([entry.id]);
      }
    }
    setSidebarState(closeNotificationsPanel);
  }, [deleteAlerts, setSidebarState, sidebarState.initialPriceAlertsExpanded, sidebarState.priceAlerts]);
};

/**
 * Repeat is an explicit ask to be told again, so this firing is over: the card goes, wherever it was
 * repeated from. Clearing `triggered_at` (but not the paused status) hands the alert back to the
 * worker's recede check, so it goes active only once the value moves off the limit and fires on the
 * next crossing — and that next firing is the one that raises a fresh toast and card.
 */
export const useRepeatPriceAlerts = () => {
  const updateAlert = useUpdateAlert();
  const clearPriceAlerts = useClearPriceAlerts();
  return useCallback(
    (entries: PriceAlertSidebarEntry[]) => {
      clearPriceAlerts(entries);
      forgetReportedPriceAlerts(entries.map((entry) => entry.id));
      for (const entry of entries) updateAlert.mutate({ id: entry.id, triggered_at: null });
    },
    [clearPriceAlerts, updateAlert],
  );
};

export const dismissProductAlertToasts = () => {
  for (const id of productAlertToastIds) {
    removeBottomRightToast(id);
  }
  productAlertToastIds.clear();
  reportedProductChangeKeys.clear();
  partialFormulaMetadataBySession.clear();
  lastProductChangeAtBySidebarKey.clear();
  toastedFolderKeys.clear();
  toastedProductChanges = [];
};

/**
 * Reading these alerts must not let the same firings toast again — a later firing carries its own
 * timestamp, so it raises a fresh toast without the reported list being emptied here.
 */
export const dismissPriceAlertToasts = () => {
  for (const id of priceAlertToastIds) {
    removeBottomRightToast(id);
  }
  priceAlertToastIds.clear();
  toastedPriceAlertFolderKeys.clear();
  toastedPriceAlerts = [];
};

export const shouldSkipAlert = (alert: TAlert): boolean => {
  const now = Date.now();
  const timestamp = alertHistory[alert.id];

  if (timestamp != null && now < timestamp + ALERT_SOUND_DELAY) {
    return true;
  }

  alertHistory[alert.id] = now;
  return false;
};

const toastMessage = (summary?: string, detail?: string) => {
  if (!summary && !detail) return undefined;
  return (
    <div className="flex flex-col gap-[4px]">
      {summary ? <p className="line-clamp-1 break-words">{summary}</p> : null}
      {detail ? <p className="line-clamp-2 break-words">{detail}</p> : null}
    </div>
  );
};

const showMoreAction = (label: string, onClick: () => void) => (
  <Button
    variant="text"
    color="primary"
    size="sm"
    onClick={(event) => {
      event.currentTarget.blur();
      onClick();
    }}
  >
    {label}
  </Button>
);

export const PriceAlertToast = ({
  alerts,
  onShowMore,
  onDismiss,
  "data-testid": testId,
}: {
  alerts: PriceAlertSidebarEntry[];
  onShowMore: () => void;
  onDismiss?: () => void;
  "data-testid"?: string;
}) => {
  const deleteAlert = useDeleteAlerts();
  const repeatAlerts = useRepeatPriceAlerts();
  const clearPriceAlerts = useClearPriceAlerts();
  const firstAlert = alerts[0];
  const showRepeatActions = alerts.length === 1 && firstAlert && !firstAlert.recurring;

  const handleClose = (alert: PriceAlertSidebarEntry) => {
    closePriceAlertToast();
    clearPriceAlerts([alert]);
    deleteAlert.mutate([alert.id]);
  };

  const handleRepeat = () => {
    closePriceAlertToast();
    repeatAlerts(alerts);
  };

  if (alerts.length > 1 && firstAlert) {
    return (
      <ToastNotification
        severity="alert"
        title={`You have ${alerts.length} new notifications`}
        onDismiss={onDismiss}
        data-testid={testId}
        actions={showMoreAction("View all", onShowMore)}
      >
        {alerts.length} price alerts were triggered
      </ToastNotification>
    );
  }

  return (
    <ToastNotification
      severity="alert"
      title={priceAlertBodyForDisplay(firstAlert?.note)}
      onDismiss={onDismiss}
      data-testid={testId}
      actions={
        showRepeatActions ? (
          <>
            <Button variant="text" color="neutral" size="sm" onClick={() => handleClose(firstAlert)}>
              Close
            </Button>
            <Button variant="outline" color="primary" size="sm" onClick={handleRepeat}>
              Repeat Alert
            </Button>
          </>
        ) : undefined
      }
    >
      {toastMessage(firstAlert?.title || undefined)}
    </ToastNotification>
  );
};

export const useHandleBroadcastCellAlerts = () => {
  const deleteAlerts = useDeleteAlerts();
  const notificationCentreEnabled = useNotificationCentreEnabled();
  const [sidebarState, setSidebarState] = useSidebarState();
  const retireReadPriceAlerts = useRetireReadPriceAlerts();
  const subsriptions = useUserPushSubscriptions();
  const products = useAllProducts();
  const { getAccessTokenSilently } = useAuth0();

  const sendPush = useMutation({
    mutationFn: async (alert: { note: string; body: string }) => {
      const body = {
        publicKey: vapidKeys.publicKey,
        privateKey: vapidKeys.privateKey,
        subscriptions: JSON.stringify(subsriptions.data?.subscriptions),
        title: `${alert.note || "Alert"} triggered`,
        body: alert.body,
      };
      const token = await getAccessTokenSilently({ authorizationParams: { audience: "hasura" } });
      await fetch("/api/send-notification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
    },
  });

  return useMutation({
    mutationFn: (alerts: TAlert[]) => {
      if (alerts.length === 0) return Promise.resolve();

      let playSound = false;

      if (!Notification) {
        console.warn("Desktop notifications not available.");
      }

      if (Notification.permission !== "granted") {
        console.warn("Notification permission not granted.");
      }

      if (!notificationCentreEnabled) {
        for (const alert of alerts) {
          if (shouldSkipAlert(alert)) {
            console.warn(`Drop duplicated alert ${alert?.id}`);
            continue;
          }

          const product = products.data?.[alert.product_id];
          const parsed = parseAlertLimit(alert.limit);
          const limitName = parsed.productId ? products.data?.[parsed.productId]?.name : undefined;
          const body = getAlertDetails(alert, product?.name ?? "", limitName, product?.packageByPackage?.name);

          if (alert.sound) {
            playSound = true;
          }

          const dismiss = registerBottomRightToastDismiss(alert.id, () => {
            removeBottomRightToast(alert.id);
            if (!alert.recurring) deleteAlerts.mutate([alert.id]);
          });
          toast.custom(() => <CellAlertToast alert={alert} onDismiss={dismiss} />, {
            ...bottomRightToastOptions(alert.id, { duration: Number.POSITIVE_INFINITY }),
          });

          if (subsriptions.data?.subscriptions?.length) {
            sendPush.mutate({ note: alert.note ?? "", body });
          }

          if (!Notification || Notification.permission !== "granted") continue;

          const notification = new Notification(`${alert.note || "Alert"} triggered`, { body });

          notification.onclick = () => {
            window.focus();
            notification.close();
            console.log("notification clicked");
          };
        }

        if (playSound) playAlertSound();
        return Promise.resolve();
      }

      // A firing the user has read or repeated is spent, so emitting it again raises nothing at all —
      // no toast, no push, and no silent card while the panel is open.
      const handled = new Set(sidebarState.handledPriceAlertFirings);
      const firings = alerts
        .map((alert) => ({ alert, entry: buildPriceAlertEntry(alert, products.data) }))
        .filter(({ entry }) => !handled.has(priceAlertFiringKey(entry)));

      if (firings.length === 0) return Promise.resolve();

      const incoming = firings.map(({ entry }) => entry);

      for (const { alert, entry } of firings) {
        if (shouldSkipAlert(alert)) {
          console.warn(`Drop duplicated alert ${alert?.id}`);
          continue;
        }

        if (alert.sound) {
          playSound = true;
        }

        if (subsriptions.data?.subscriptions?.length) {
          sendPush.mutate({ note: entry.note, body: entry.title });
        }

        if (!Notification || Notification.permission !== "granted") continue;

        const notification = new Notification(`${entry.note} triggered`, { body: entry.title });

        notification.onclick = () => {
          window.focus();
          notification.close();
          console.log("notification clicked");
        };
      }

      const isReportable = incoming.some((entry) => {
        const key = priceAlertFiringKey(entry);
        if (reportedPriceAlertFirings.has(key)) return false;
        reportedPriceAlertFirings.add(key);
        return true;
      });
      // No toast over the open panel: the user is already looking at the list it would cover.
      const shouldShowToast = isReportable && !sidebarState.isOpen;

      setSidebarState((prev) => ({
        ...prev,
        priceAlerts: mergePriceAlertRows(prev.priceAlerts, incoming),
      }));

      if (shouldShowToast) {
        const toastAlerts = mergePriceAlertRows(
          priceAlertToastIds.has(PRICE_ALERT_TOAST_ID) ? toastedPriceAlerts : [],
          incoming,
        );
        toastedPriceAlerts = toastAlerts;
        priceAlertToastIds.add(PRICE_ALERT_TOAST_ID);
        toastedPriceAlertFolderKeys.clear();
        for (const entry of toastAlerts) {
          if (entry.packageId) toastedPriceAlertFolderKeys.add(entry.packageId);
        }

        const dismissToast = registerBottomRightToastDismiss(PRICE_ALERT_TOAST_ID, () => {
          closePriceAlertToast();
          const toRetire = priceAlertsToRetireOnToastDismiss(toastAlerts);
          if (toRetire.length > 0) retireReadPriceAlerts(toRetire);
        });

        toast.custom(
          () => (
            <PriceAlertToast
              alerts={toastAlerts}
              onDismiss={dismissToast}
              data-testid={`alert-toast-${PRICE_ALERT_TOAST_ID}`}
              onShowMore={() => {
                setSidebarState((prev) => ({
                  ...prev,
                  isOpen: true,
                  priceAlerts: mergePriceAlertRows(prev.priceAlerts, toastAlerts),
                  initialWhatsNewExpanded: prev.isOpen ? prev.initialWhatsNewExpanded : false,
                  initialPriceAlertsExpanded: true,
                }));
                closePriceAlertToast();
              }}
            />
          ),
          bottomRightToastOptions(PRICE_ALERT_TOAST_ID, { duration: Number.POSITIVE_INFINITY }),
        );
      }

      if (playSound) playAlertSound();
      return Promise.resolve();
    },
  });
};

const ProductChangedToast = ({
  products,
  onShowMore,
  onDismiss,
  "data-testid": testId,
}: {
  products: ProductChangeAlert[];
  onShowMore: () => void;
  onDismiss: () => void;
  "data-testid"?: string;
}) => {
  const productInfo = useAllProducts().data;
  const curveCount = productChangeToastCurveCount(products);
  const alert = products[0];
  const singleCopy =
    curveCount === 1 && alert
      ? getCurveChangeToastCopy(
          alert,
          (alert.productId ? productInfo?.[alert.productId]?.name : undefined) ?? alert.name,
          (alert.productId ? productInfo?.[alert.productId]?.packageByPackage?.name : undefined) ?? "Unknown folder",
        )
      : undefined;

  return (
    <ToastNotification
      severity="alert"
      title={productChangeToastTitle(curveCount)}
      onDismiss={onDismiss}
      data-testid={testId}
      actions={showMoreAction("View Changes", onShowMore)}
    >
      {toastMessage(
        curveCount === 1 ? singleCopy?.header : formatCurveNamesSummary(products) || undefined,
        curveCount === 1 ? singleCopy?.body : undefined,
      )}
    </ToastNotification>
  );
};

export const alertProductChanged = (products: Array<{ name: string }>) => {
  const alertProducts = products;
  if (alertProducts.length === 0) return;

  const title = alertProducts.length > 1 ? "Products changed" : "Product changed";
  const list = alertProducts.map((p, i) => <div key={i.toString()}>{p.name}</div>);
  // An empty id is treated as no id, which would stack a new toast per batch instead of replacing.
  const id = alertProducts.map((x) => x.name).join("") || "product-changed";
  const dismiss = registerBottomRightToastDismiss(id, () => {
    removeBottomRightToast(id);
  });
  toast.custom(
    () => (
      <ToastNotification severity="alert" title={title} onDismiss={dismiss} data-testid={`alert-toast-${id}`}>
        <div>{list}</div>
      </ToastNotification>
    ),
    bottomRightToastOptions(id, { duration: Number.POSITIVE_INFINITY }),
  );
};

export const useProductChangedAlert = () => {
  const notificationCentreEnabled = useNotificationCentreEnabled();
  const [, setSidebarState] = useSidebarState();

  return useCallback(
    (products: ProductChangeAlert[]) => {
      if (products.length === 0) return;

      if (!notificationCentreEnabled) {
        // The legacy toast only names curves, so permission changes (whose cards deliberately
        // name nothing) have nothing to show there.
        alertProductChanged(splitProductChangeAlerts(products).curveChanges);
        return;
      }

      const now = Date.now();
      const incoming = collapseProductChangeAlerts(upgradeIncomingProductChanges(products));
      const { curveChanges: incomingCurves } = splitProductChangeAlerts(incoming);

      // The sidebar merge below can't reach an already-rendered toast, so close it here.
      const lostFolderKeys = lostFolderPackageIds(incoming);
      if (lostFolderKeys.some((packageId) => toastedFolderKeys.has(packageId))) {
        dismissProductAlertToast(PRODUCT_CHANGE_TOAST_ID);
      }
      if (lostFolderKeys.some((packageId) => toastedPriceAlertFolderKeys.has(packageId))) {
        closePriceAlertToast();
      }

      const toastIsOpen = productAlertToastIds.has(PRODUCT_CHANGE_TOAST_ID);
      const shouldShowToast = incomingCurves.some((item) => {
        const sessionKey = productChangeSessionKey(item);
        const sidebarKey = productChangeSidebarKey(item);
        const lastAt = lastProductChangeAtBySidebarKey.get(sidebarKey) ?? 0;
        if (now - lastAt > PARTIAL_FORMULA_METADATA_MS) {
          reportedProductChangeKeys.delete(sessionKey);
        }
        if (reportedProductChangeKeys.has(sessionKey)) return false;
        reportedProductChangeKeys.add(sessionKey);
        return true;
      });

      setSidebarState((prev) => ({
        ...prev,
        products: mergeProductChangeAlertRows(prev.products, incoming, lastProductChangeAtBySidebarKey, now),
        priceAlerts: dropPriceAlertsForFolders(prev.priceAlerts, lostFolderKeys),
      }));

      // An already-visible toast still needs the later edit of the same curve, even when we
      // wouldn't raise a fresh toast for that session key.
      if (incomingCurves.length === 0 || (!shouldShowToast && !toastIsOpen)) return;

      const toastProducts = aggregateOpenProductChangeToast(toastIsOpen ? toastedProductChanges : [], incomingCurves);
      toastedProductChanges = toastProducts;

      productAlertToastIds.add(PRODUCT_CHANGE_TOAST_ID);
      toastedFolderKeys.clear();
      for (const item of toastProducts) {
        if (item.packageId) toastedFolderKeys.add(item.packageId);
      }

      const dismiss = registerBottomRightToastDismiss(PRODUCT_CHANGE_TOAST_ID, () => {
        dismissProductAlertToast(PRODUCT_CHANGE_TOAST_ID);
      });
      toast.custom(
        () => (
          <ProductChangedToast
            products={toastProducts}
            onDismiss={dismiss}
            data-testid={`alert-toast-${PRODUCT_CHANGE_TOAST_ID}`}
            onShowMore={() => {
              const expandCurve = toastProducts.some((item) => isCurveChangeType(item.changeType));
              const expandPermission = incoming.some((item) => isPermissionChangeType(item.changeType));
              setSidebarState((prev) => ({
                ...prev,
                isOpen: true,
                products: mergeProductChangeAlertRows(prev.products, incoming, lastProductChangeAtBySidebarKey, now),
                initialWhatsNewExpanded: prev.isOpen ? prev.initialWhatsNewExpanded : false,
                initialCurveChangesExpanded: expandCurve,
                initialPermissionChangesExpanded: expandPermission,
              }));
              dismissProductAlertToast(PRODUCT_CHANGE_TOAST_ID);
            }}
          />
        ),
        bottomRightToastOptions(PRODUCT_CHANGE_TOAST_ID, { duration: Number.POSITIVE_INFINITY }),
      );
    },
    [notificationCentreEnabled, setSidebarState],
  );
};

export const useTriggerFailedEditAlert = () => {
  return useCallback(() => {
    const id = `manual-cell-update-failed-${crypto.randomUUID()}`;
    registerBottomRightToastDismiss(id, () => {
      removeBottomRightToast(id);
    });
    toast.custom(
      () => (
        <Alert
          variant="danger"
          title={"Manual cell update failed"}
          message={[
            <p key="m1">Please check your internet connection and try again.</p>,
            <p key="m2" className="m-0">
              {"If the issue persists, please "}
              <a href="mailto:support@artis.works" className="underline text-foreground-muted">
                Contact Support.
              </a>
            </p>,
          ]}
        />
      ),
      bottomRightToastOptions(id),
    );
  }, []);
};

const ANNOUNCEMENT_TOAST_ID = "announcement-batch";
const ANNOUNCEMENT_TOAST_TITLE = "What's new";

/**
 * Announcements whose toast the user has already closed. Dismissing persists `dismissed_at`, but
 * viewing (all) only opens the panel or modal — without this, the effect below would put the toast
 * straight back the next time it re-runs.
 */
const closedAnnouncementToastIds = new Set<string>();

const AnnouncementToast = ({
  announcements,
  onView,
  onViewAll,
  onDismiss,
  "data-testid": testId,
}: {
  announcements: Array<WhatsNewFeature & { _id: string; title: string }>;
  onView: (announcement: WhatsNewFeature & { _id: string; title: string }) => void;
  onViewAll: () => void;
  onDismiss: () => void;
  "data-testid"?: string;
}) => {
  const first = announcements[0];
  if (!first) return null;

  if (announcements.length === 1) {
    return (
      <ToastNotification
        severity="info"
        title={ANNOUNCEMENT_TOAST_TITLE}
        onDismiss={onDismiss}
        data-testid={testId}
        actions={
          <Button variant="outline" color="primary" size="sm" onClick={() => onView(first)}>
            View
          </Button>
        }
      >
        {toastMessage(first.title, first.description ?? undefined)}
      </ToastNotification>
    );
  }

  return (
    <ToastNotification
      severity="info"
      title={`${announcements.length} new announcements`}
      onDismiss={onDismiss}
      data-testid={testId}
      actions={showMoreAction("View all", onViewAll)}
    >
      {first.title}
    </ToastNotification>
  );
};

/**
 * Shows a toast for announcements the user hasn't viewed or dismissed yet. Unlike the other
 * toasts here, eligibility is driven entirely by persisted `viewed_at`/`dismissed_at` rows
 * rather than in-memory session tracking — dismissing (or viewing) an announcement removes it
 * from `useToastableAnnouncements` for good, so there's no need to remember what's already
 * been shown across reloads.
 */
export const useAnnouncementToast = () => {
  const notificationCentreEnabled = useNotificationCentreEnabled();
  const userId = useUserId();
  const hasura = useGraphQLClient();
  const queryClient = useQueryClient();
  const viewedAnnouncements = useViewedAnnouncements();
  const toastable = useToastableAnnouncements();
  const [, setSidebarState] = useSidebarState();
  const [modalFeature, setModalFeature] = useState<(WhatsNewFeature & { _id: string; title: string }) | null>(null);

  useEffect(() => {
    const pending = toastable.filter((item) => !closedAnnouncementToastIds.has(item._id));

    if (!notificationCentreEnabled || pending.length === 0) {
      removeBottomRightToast(ANNOUNCEMENT_TOAST_ID);
      return;
    }

    const closeToast = () => {
      removeBottomRightToast(ANNOUNCEMENT_TOAST_ID);
      for (const item of pending) closedAnnouncementToastIds.add(item._id);
    };

    /**
     * Persists `dismissed_at` for everything this toast covered, so it stays gone after a reload
     * rather than only for this session. It says nothing about whether the user read them —
     * `viewed_at` is what the What's new count and card styling key off.
     */
    const dismissPending = () => {
      closeToast();
      const announcements = pending.map((item) => ({ announcement_id: item._id, title: item.title }));
      dismissAnnouncementToasts(userId, announcements, viewedAnnouncements.data ?? [], hasura, queryClient);
    };

    const handleView = (announcement: WhatsNewFeature & { _id: string; title: string }) => {
      closeToast();
      insertViewedAnnouncement(
        userId,
        { announcement_id: announcement._id, title: announcement.title, viewed_at: new Date().toISOString() },
        viewedAnnouncements.data ?? [],
        hasura,
        queryClient,
      );
      setModalFeature(announcement);
    };

    const handleViewAll = () => {
      // Opening the panel shows the same announcements, so the toast has done its job for good.
      dismissPending();
      setSidebarState((prev) => ({
        ...prev,
        isOpen: true,
        initialWhatsNewExpanded: true,
      }));
    };

    const dismiss = registerBottomRightToastDismiss(ANNOUNCEMENT_TOAST_ID, dismissPending);
    toast.custom(
      () => (
        <AnnouncementToast
          announcements={pending}
          onView={handleView}
          onViewAll={handleViewAll}
          onDismiss={dismiss}
          data-testid={`alert-toast-${ANNOUNCEMENT_TOAST_ID}`}
        />
      ),
      bottomRightToastOptions(ANNOUNCEMENT_TOAST_ID, { duration: Number.POSITIVE_INFINITY }),
    );
  }, [notificationCentreEnabled, toastable, viewedAnnouncements.data, userId, hasura, queryClient, setSidebarState]);

  return modalFeature ? <WhatsNewModal feature={modalFeature} onClose={() => setModalFeature(null)} /> : null;
};

function playAlertSound() {
  if (document.getElementById("alert-sound")) return;
  const audio = document.createElement("audio");
  audio.id = "alert-sound";
  audio.src = alertSounds.huthut;
  document.body.appendChild(audio);
  audio.play();
  audio.addEventListener("ended", () => {
    document.getElementById("alert-sound")?.remove();
  });
}
